import { API_ENDPOINTS } from "config/config";
import { buildAnalyticsQueryParams } from "utils/analyticsQueryParams";

export const getGhlStatus = async (api) => {
  const response = await api.get(API_ENDPOINTS.GHL_STATUS);
  return response.data.data;
};

export const getGhlPipelineMetrics = async (api, params = {}) => {
  const response = await api.get(API_ENDPOINTS.GHL_PIPELINE_METRICS, {
    params: buildAnalyticsQueryParams(params),
  });
  return response.data.data;
};

export const getGhlOpportunities = async (api, { page = 1, limit = 10, search } = {}) => {
  const params = { page, limit };
  if (search) params.search = search;
  const response = await api.get(API_ENDPOINTS.GHL_OPPORTUNITIES, { params });
  return response.data.data;
};

export const getGhlEmailMetrics = async (api, { limit = 30, filters, refresh = false } = {}) => {
  const params = buildAnalyticsQueryParams({ filters });
  params.limit = limit;
  if (refresh) params.refresh = "true";

  const response = await api.get(API_ENDPOINTS.GHL_EMAIL_METRICS, { params });
  return response.data.data;
};

export const getGhlEmailStats = async (api, { filters, source = "all" } = {}) => {
  const params = buildAnalyticsQueryParams({ filters });
  params.source = source || "all";
  const response = await api.get(API_ENDPOINTS.GHL_EMAIL_STATS, { params });
  return response.data.data;
};

export const syncGhlEmailMetrics = async (api, { filters } = {}) => {
  const response = await api.post(API_ENDPOINTS.GHL_SYNC_EMAIL_METRICS, null, {
    params: buildAnalyticsQueryParams({ filters }),
  });
  return {
    data: response.data.data,
    message: response.data.message,
  };
};

const postGhlSync = async (api, endpoint, { full = false } = {}) => {
  const response = await api.post(
    endpoint,
    {},
    { params: { full: full ? "true" : "false" } }
  );
  return {
    data: response.data.data,
    message: response.data.message,
  };
};

export const syncGhlPipelineMetrics = async (api, options) =>
  postGhlSync(api, API_ENDPOINTS.GHL_SYNC_PIPELINE_METRICS, options);

export const syncGhlContacts = async (api, options) =>
  postGhlSync(api, API_ENDPOINTS.GHL_SYNC_CONTACTS, options);

export const syncGhlOpportunities = async (api, options) =>
  postGhlSync(api, API_ENDPOINTS.GHL_SYNC_OPPORTUNITIES, options);

export const syncGhlAppointments = async (api, options) =>
  postGhlSync(api, API_ENDPOINTS.GHL_SYNC_APPOINTMENTS, options);

export const getGhlDebugInfo = async (api) => {
  const response = await api.get(API_ENDPOINTS.GHL_DEBUG);
  return response.data.data;
};

const ghlSyncRequestConfig = { showLoader: false };

export const syncGhlFunnel = async (api, { locationId } = {}) => {
  const body = { syncType: "funnel" };
  if (locationId) body.locationId = locationId;

  const response = await api.post(API_ENDPOINTS.GHL_SYNC_FUNNEL, body, ghlSyncRequestConfig);
  const payload = response.data ?? {};

  return {
    success: payload.success !== false,
    requiresOtp: payload.requiresOtp === true,
    message: payload.message,
    data: payload.data,
    traceId: payload.traceId,
  };
};

export const verifyGhlFunnelOtp = async (api, { otp, locationId } = {}) => {
  const body = { syncType: "funnel", otp };
  if (locationId) body.locationId = locationId;

  const response = await api.post(
    API_ENDPOINTS.GHL_SYNC_FUNNEL_VERIFY_OTP,
    body,
    ghlSyncRequestConfig
  );
  const payload = response.data ?? {};

  return {
    success: payload.success !== false,
    authenticated: payload.authenticated === true,
    message: payload.message,
    data: payload.data,
  };
};

export const syncGhlEmail = async (api, { locationId } = {}) => {
  const body = { syncType: "email" };
  if (locationId) body.locationId = locationId;

  const response = await api.post(API_ENDPOINTS.GHL_SYNC_EMAIL, body, ghlSyncRequestConfig);
  const payload = response.data ?? {};

  return {
    success: payload.success !== false,
    requiresOtp: payload.requiresOtp === true,
    message: payload.message,
    data: payload.data,
    traceId: payload.traceId,
  };
};

export const verifyGhlEmailOtp = async (api, { otp, locationId } = {}) => {
  const body = { syncType: "email", otp };
  if (locationId) body.locationId = locationId;

  const response = await api.post(
    API_ENDPOINTS.GHL_SYNC_EMAIL_VERIFY_OTP,
    body,
    ghlSyncRequestConfig
  );
  const payload = response.data ?? {};

  return {
    success: payload.success !== false,
    authenticated: payload.authenticated === true,
    message: payload.message,
    data: payload.data,
  };
};

export const getGhlApiErrorMessage = (error, fallback = "Request failed. Please try again.") => {
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (!error?.response) return "Network error. Check your connection and try again.";
  return fallback;
};
